// Meetings.jsx — Weekly meeting agendas, tasks, and accountability tracker.
import React, { useEffect, useState, useCallback } from "react";
import { api, formatErr } from "./lib/api";
import { useAuth } from "./lib/auth";
import { confirmWithUndo, showToast } from "./lib/theme";
import { AssigneeSelect } from "./lib/users";
import DateField from "./components/DateField";
import WeeklyBoardTeamView from "./WeeklyBoardTeamView";

const STATUS_LABELS = {
  open: { lbl: "🆕 جديدة", color: "var(--info)" },
  in_progress: { lbl: "⏳ جارية", color: "var(--warn)" },
  done: { lbl: "✅ منجزة", color: "var(--success)" },
  blocked: { lbl: "🚧 معطّلة", color: "var(--danger)" },
};

const PRIORITY_COLOR = { high: "var(--danger)", medium: "var(--warn)", low: "var(--muted)" };

export default function Meetings() {
  const { user } = useAuth();
  const canCreate = true;
  const canEdit = user.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "اجتماع أسبوعي", date: new Date().toISOString().slice(0, 10), attendees: "", agenda: "", notes: "" });
  const [expanded, setExpanded] = useState(null);
  const [taskForm, setTaskForm] = useState({ description: "", assignee: "", due_date: "", priority: "medium" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/meetings");
      setItems(data.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: form.title.trim(),
        date: form.date,
        attendees: form.attendees.split(",").map((s) => s.trim()).filter(Boolean),
        agenda: form.agenda.split("\n").map((s) => s.trim()).filter(Boolean),
        notes: form.notes,
      };
      await api.post("/meetings", payload);
      showToast({ kind: "success", message: "✅ تم إنشاء الاجتماع" });
      setForm({ title: "اجتماع أسبوعي", date: new Date().toISOString().slice(0, 10), attendees: "", agenda: "", notes: "" });
      setShowForm(false);
      load();
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) || e.message });
    }
  };

  const addTask = async (mid) => {
    if (!taskForm.description.trim() || !taskForm.assignee.trim()) {
      showToast({ kind: "danger", message: "أدخل الوصف والمسؤول" });
      return;
    }
    try {
      await api.post(`/meetings/${mid}/tasks`, taskForm);
      setTaskForm({ description: "", assignee: "", due_date: "", priority: "medium" });
      load();
    } catch (e) {
      showToast({ kind: "danger", message: formatErr(e.response?.data?.detail) });
    }
  };

  const updateTask = async (mid, tid, updates) => {
    await api.patch(`/meetings/${mid}/tasks/${tid}`, updates);
    load();
  };

  const deleteTask = (mid, tid) => {
    confirmWithUndo({
      message: "🗑 سيتم حذف المهمة",
      onConfirm: async () => { await api.delete(`/meetings/${mid}/tasks/${tid}`); load(); },
      onUndo: () => {},
    });
  };

  const deleteMeeting = (m) => {
    setItems((cur) => cur.filter((x) => x.id !== m.id));
    confirmWithUndo({
      message: `🗑 سيتم حذف اجتماع "${m.title}" (${m.date})`,
      onConfirm: async () => { try { await api.delete(`/meetings/${m.id}`); } catch (e) {} load(); },
      onUndo: load,
    });
  };

  return (
    <div data-testid="meetings-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* v194 Phase 2 — بنود الاجتماع الأسبوعي القادم (للفريق، للقراءة فقط) */}
      <WeeklyBoardTeamView />
      <div className="card" style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <div>
          <h3 style={{ margin: "0 0 4px", color: "var(--brown-deep)" }}>📋 اجتماعات الفريق</h3>
          <p style={{ margin: 0, fontSize: 12, color: "var(--muted)" }}>أجندة كل اجتماع + المتطلبات والمسؤولين بتاريخ مرجعي</p>
        </div>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={() => setShowForm(!showForm)} data-testid="toggle-meeting-form">
            {showForm ? "إلغاء" : "+ اجتماع جديد"}
          </button>
        )}
      </div>

      {showForm && canCreate && (
        <form className="card" onSubmit={submit} data-testid="meeting-form" style={{ marginBottom: 14 }}>
          <h4 style={{ margin: "0 0 10px", color: "var(--brown-deep)" }}>➕ اجتماع جديد</h4>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 8 }}>
            <div><label style={{ fontSize: 11 }}>العنوان *</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="mt-title" style={{ width: "100%" }} /></div>
            <div><label style={{ fontSize: 11 }}>التاريخ *</label><DateField  required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="mt-date" style={{ width: "100%" }} /></div>
          </div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>الحضور (مفصولين بفاصلة)</label><input value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} placeholder="خالد, سارة, محمد" data-testid="mt-attendees" style={{ width: "100%" }} /></div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>الأجندة (كل بند في سطر)</label><textarea rows={4} value={form.agenda} onChange={(e) => setForm({ ...form, agenda: e.target.value })} data-testid="mt-agenda" style={{ width: "100%" }} placeholder="مراجعة مبيعات الأسبوع&#10;الحملات القادمة&#10;المخزون والمشتريات" /></div>
          <div style={{ marginTop: 8 }}><label style={{ fontSize: 11 }}>ملاحظات</label><textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="mt-notes" style={{ width: "100%" }} /></div>
          <button type="submit" className="btn btn-success" data-testid="mt-submit" style={{ marginTop: 10 }}>💾 احفظ الاجتماع</button>
        </form>
      )}

      {loading ? <div style={{ padding: 20, textAlign: "center" }}>⏳</div> : items.length === 0 ? (
        <div className="card" style={{ padding: 20, textAlign: "center", color: "var(--muted)" }}>لا توجد اجتماعات مسجّلة بعد</div>
      ) : items.map((m) => (
        <div key={m.id} className="card" style={{ marginBottom: 10 }} data-testid={`mt-${m.id}`}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <h4 style={{ margin: 0, color: "var(--brown-deep)" }}>📅 {m.title}</h4>
              <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                {m.date} {m.attendees?.length > 0 && `· 👥 ${m.attendees.join("، ")}`}
                {m.tasks?.length > 0 && ` · ✅ ${m.tasks.filter(t => t.status === "done").length}/${m.tasks.length} مهام منجزة`}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button type="button" className="btn btn-soft" onClick={() => setExpanded(expanded === m.id ? null : m.id)} data-testid={`mt-toggle-${m.id}`} style={{ fontSize: 11 }}>
                {expanded === m.id ? "🔼" : "🔽 تفاصيل"}
              </button>
              {canEdit && <button type="button" onClick={() => deleteMeeting(m)} className="btn" data-testid={`mt-delete-${m.id}`} style={{ background: "var(--danger)", color: "#fff", fontSize: 11 }}>🗑</button>}
            </div>
          </div>

          {expanded === m.id && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px dashed var(--border)" }}>
              {m.agenda?.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <b style={{ fontSize: 12, color: "var(--brown-deep)" }}>📌 الأجندة:</b>
                  <ul style={{ margin: "4px 0 0", paddingInlineStart: 18, fontSize: 12 }}>
                    {m.agenda.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
              {m.notes && (user.role === "admin" || user.role === "ceo") && (
                <details style={{ marginBottom: 10, background: "var(--beige)", padding: 8, borderRadius: 6, border: "1px solid var(--border)" }} data-testid="meeting-notes-fold">
                  <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 700, color: "var(--brown-deep)", padding: "2px 4px" }}>
                    📝 الأجندة الموسّعة للمدير <span style={{ fontSize: 10, color: "var(--muted)", fontWeight: 400 }}>(خاصة بك فقط — اضغط لعرضها)</span>
                  </summary>
                  <div style={{ marginTop: 8, padding: 10, background: "#fff", borderRadius: 4, fontSize: 12, whiteSpace: "pre-wrap", maxHeight: 480, overflow: "auto", lineHeight: 1.8, fontFamily: "inherit", color: "var(--text)" }}>
                    {m.notes}
                  </div>
                </details>
              )}

              <b style={{ fontSize: 12, color: "var(--brown-deep)", display: "block", marginBottom: 6 }}>✅ المتطلبات / المهام ({m.tasks?.length || 0})</b>

              {m.tasks?.length > 0 && (
                <div className="tw" style={{ marginBottom: 10 }}>
                  <table style={{ fontSize: 12 }}>
                    <thead><tr><th>الوصف</th><th>المسؤول</th><th>الموعد</th><th>الأولوية</th><th>الحالة</th><th></th></tr></thead>
                    <tbody>
                      {m.tasks.map((t) => {
                        const status = STATUS_LABELS[t.status] || STATUS_LABELS.open;
                        return (
                          <tr key={t.id} data-testid={`task-${t.id}`}>
                            <td>{t.description}</td>
                            <td><b>{t.assignee}</b></td>
                            <td style={{ fontSize: 11 }}>{t.due_date || "—"}</td>
                            <td><span style={{ padding: "2px 6px", background: PRIORITY_COLOR[t.priority], color: "#fff", borderRadius: 3, fontSize: 10 }}>{t.priority}</span></td>
                            <td>
                              {canEdit ? (
                                <select value={t.status || "open"} onChange={(e) => updateTask(m.id, t.id, { status: e.target.value })} data-testid={`task-status-${t.id}`} style={{ fontSize: 11 }}>
                                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v.lbl}</option>)}
                                </select>
                              ) : <span style={{ padding: "2px 6px", background: status.color, color: "#fff", borderRadius: 3, fontSize: 10 }}>{status.lbl}</span>}
                            </td>
                            <td>{canEdit && <button onClick={() => deleteTask(m.id, t.id)} className="btn" data-testid={`task-delete-${t.id}`} style={{ background: "transparent", color: "var(--danger)", fontSize: 12, padding: "2px 6px" }}>×</button>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {canEdit && (
                <div style={{ background: "var(--beige)", padding: 8, borderRadius: 4, display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto auto", gap: 6, alignItems: "end" }}>
                  <div><label style={{ fontSize: 10 }}>وصف المهمة</label><input value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} data-testid="new-task-desc" style={{ width: "100%", fontSize: 12 }} /></div>
                  <AssigneeSelect value={taskForm.assignee} onChange={(v) => setTaskForm({ ...taskForm, assignee: v })} label="المسؤول" testid="new-task-assignee" />
                  <div><label style={{ fontSize: 10 }}>الموعد</label><DateField  value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} data-testid="new-task-due" style={{ width: "100%", fontSize: 12 }} /></div>
                  <select value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} data-testid="new-task-priority" style={{ fontSize: 11 }}>
                    <option value="low">منخفضة</option><option value="medium">متوسطة</option><option value="high">عالية</option>
                  </select>
                  <button type="button" className="btn btn-success" onClick={() => addTask(m.id)} data-testid={`add-task-${m.id}`} style={{ fontSize: 11 }}>+ إضافة</button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
